import type { Namespace } from '../types';

// The shared vocabulary for a site's status, used by BOTH the site switcher and
// `/admin/sites` via `features/src/shared/site-status.ts`.
//
// Replaces the `ui.cloudSite*` keys, which were wrong twice over: they hardcoded
// "Cloud site" onto every site including local ones, and they put the HEALTH word
// where an operator reads a REACHABILITY word — so "critical" (an open alarm) and
// "offline" (the box not answering) looked like the console contradicting itself.
//
// Each word here names its axis. Nothing says just "offline" any more, because
// that single word was the whole confusion.
//
// DE drafted, queued for native review like the other catalogs.
export const siteStatus = {
  // ── reachability: can the cloud reach the box? ─────────────────────────────
  reachOnline: { de: 'erreichbar', en: 'reachable' },
  reachOffline: { de: 'nicht erreichbar', en: 'unreachable' },
  reachNoBox: { de: 'kein Server registriert', en: 'no server registered' },

  // ── health: is the farm in trouble? ───────────────────────────────────────
  healthOk: { de: 'keine Alarme', en: 'no alarms' },
  healthWarning: { de: 'Warnungen offen', en: 'warnings open' },
  healthCritical: { de: 'kritische Alarme', en: 'critical alarms' },
  // `stale` is the state that genuinely means "we stopped hearing" — distinct
  // from `unreachable`, which is about the box rather than its telemetry.
  healthNoSignal: { de: 'kein Signal', en: 'no signal' },

  // ── the administrative flag, which outranks both axes ─────────────────────
  disabled: { de: 'deaktiviert', en: 'disabled' },
  disabledHint: {
    de: 'Von einem Administrator deaktiviert — der Status des Standorts wird nicht bewertet, bis er wieder aktiviert wird.',
    en: 'Switched off by an administrator — the site is not assessed until it is re-enabled.',
  },

  // ── column headers / labels ───────────────────────────────────────────────
  colReachability: { de: 'Erreichbarkeit', en: 'Reachability' },
  colHealth: { de: 'Zustand', en: 'Health' },

  // Shown when a viewer cannot see the reachability axis at all (it needs admin).
  reachUnknownHint: {
    de: 'Erreichbarkeit ist nur für Administratoren sichtbar',
    en: 'Reachability is visible to administrators only',
  },
  // The switcher's subtitle when health has not loaded yet.
  unknown: { de: 'Status unbekannt', en: 'Status unknown' },
  // Last-known qualifier, used when the box is unreachable so its health cannot
  // be current. Without this the console asserts a live fault it cannot observe.
  lastKnown: { de: 'zuletzt bekannt: {state}', en: 'last known: {state}' },
} satisfies Namespace;
